import React from "react";

import {
  KintoneExportReport,
  KintoneExportSetting,
  KintoneFormField,
  KintoneFormFields,
  ProcedureType,
} from "@graffer-inc/cloud-gov-core";
import {
  InlineRadioButton,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  TextField,
} from "@graffer-inc/console-design-kit";
import { IFormSchemaV2 } from "@graffer-inc/form-schema";
import { UpdateKintoneExportSettingInput } from "@graffer-inc/smart-apply";

import {
  KintoneExportSettingGroupPermissionAttached,
  validateDescription,
  validateName,
  validateUpdateSettingInput,
} from "../../../../../../application/domain/kintone-export";
import { useAuthContextV2 } from "../../../../../../auth-state";
import { useInputProps } from "../../../common-hook";
import {
  ElementFieldMappingsForm,
  MetadataFieldMappingForm,
} from "./element-field-mappings-form";
import { useSettingInfoOperations, useManualExport } from "./hook";
import { KintoneAPITokenInput } from "./kintone-api-token-input";
import { KintoneFieldsViewer } from "./kintone-fields-viewer";
import { ExportModal } from "./export-modal";

import type { UpdateKintoneExportSettingFuncType } from "../../../../../pages/data-transfer/kintone-export/page-hooks/kintone-export-setting-detail/hook";
import { ManualExportSubmissionFuncType } from "../../../common-hook";

type Props = {
  kintoneExportSetting: KintoneExportSetting;
  procedure: ProcedureType;
  kintoneFormFieldsInfo: {
    kintoneFormFields?: KintoneFormFields;
    errorMessage?: string;
  };
  procedureDetailLink: string;
  onClickUpdate: UpdateKintoneExportSettingFuncType;
  onClickExport: ManualExportSubmissionFuncType;
  moveToReportDetail: (reportId: KintoneExportReport["id"]) => void;

  showJsonEditor?: boolean;
  showKintoneFieldsList?: boolean;
};

export const SettingInfo: React.FC<Props> = ({
  kintoneExportSetting,
  procedure,
  kintoneFormFieldsInfo,
  procedureDetailLink,
  onClickUpdate,
  onClickExport,
  moveToReportDetail,
  showJsonEditor,
  showKintoneFieldsList,
}) => {
  const formSchema = React.useMemo(() => {
    return (
      procedure.published?.template.formSchema ??
      procedure.draft.template.formSchema
    );
  }, [procedure]);
  const {
    handleClickUpdate,
    isUpdating,
    input,
    handleChange,
    shouldDisplayMappingError,
    setShouldDisplayMappingError,
    hasChanged,
  } = useSettingInfoOperations({
    kintoneExportSetting,
    onClickUpdate,
    onClickExport,
    moveToReportDetail,
    formSchema,
    kintoneFormFields: kintoneFormFieldsInfo.kintoneFormFields,
  });

  const {
    isExporting,
    applicationIds,
    handleClickExport,
    errorMessage,
    onChangeApplicationIds,
    isExportingModalOpen,
    onCloseExportModal,
    setIsExportingModalOpen,
  } = useManualExport(
    hasChanged,
    kintoneExportSetting,
    formSchema,
    onClickExport,
    moveToReportDetail,
    setShouldDisplayMappingError,
    kintoneFormFieldsInfo.kintoneFormFields,
  );

  return (
    <section>
      {kintoneFormFieldsInfo.errorMessage && (
        <KintoneFieldsErrorMessage
          errorMessage={kintoneFormFieldsInfo.errorMessage}
        />
      )}
      <Buttons
        onClickUpdate={handleClickUpdate}
        isUpdating={isUpdating}
        updateKintoneExportSettingInput={input}
        kintoneFormFields={kintoneFormFieldsInfo.kintoneFormFields}
        formSchema={formSchema}
        isExporting={isExporting}
        setShouldDisplayMappingError={setShouldDisplayMappingError}
        setIsExportingModalOpen={setIsExportingModalOpen}
      />
      <UpdateForm
        procedure={procedure}
        kintoneFormFields={kintoneFormFieldsInfo.kintoneFormFields}
        updateKintoneExportSettingInput={input}
        onChange={handleChange}
        kintoneAppId={kintoneExportSetting.kintoneAppId}
        kintoneSubdomain={kintoneExportSetting.kintoneSubdomain}
        procedureDetailLink={procedureDetailLink}
        shouldDisplayMappingError={shouldDisplayMappingError}
        showJsonEditor={showJsonEditor}
        showKintoneFieldsList={showKintoneFieldsList}
      />
      <ExportModal
        isOpen={isExportingModalOpen}
        onClose={onCloseExportModal}
        applicationIds={applicationIds}
        onChangeApplicationIds={onChangeApplicationIds}
        errorMessage={errorMessage}
        onExecute={handleClickExport}
      />
    </section>
  );
};

const KintoneFieldsErrorMessage: React.FC<{ errorMessage: string }> = ({
  errorMessage,
}) => {
  return (
    <div className="columns is-vcentered mb-5">
      <div className="column">
        <div className="notification is-danger is-light">{errorMessage}</div>
      </div>
    </div>
  );
};

type ButtonsProps = {
  onClickUpdate: () => void;
  isUpdating: boolean;
  updateKintoneExportSettingInput: UpdateKintoneExportSettingInput;
  formSchema: IFormSchemaV2;
  kintoneFormFields?: KintoneFormFields;
  isExporting: boolean;
  setShouldDisplayMappingError: (v: boolean) => void;
  setIsExportingModalOpen: (v: boolean) => void;
};

const Buttons: React.FC<ButtonsProps> = ({
  onClickUpdate,
  isUpdating,
  updateKintoneExportSettingInput,
  formSchema,
  kintoneFormFields,
  isExporting,
  setShouldDisplayMappingError,
  setIsExportingModalOpen,
}) => {
  const [showErrorMessage, setShowErrorMessage] = React.useState(false);
  const handleUpdate = React.useCallback(() => {
    if (
      validateUpdateSettingInput(
        updateKintoneExportSettingInput,
        formSchema,
        kintoneFormFields,
      )
    ) {
      setShowErrorMessage(false);
      setShouldDisplayMappingError(false);
      onClickUpdate();
      return;
    }
    setShowErrorMessage(true);
    if (!updateKintoneExportSettingInput.disabled) {
      setShouldDisplayMappingError(true);
    }
  }, [
    updateKintoneExportSettingInput,
    formSchema,
    kintoneFormFields,
    setShowErrorMessage,
    onClickUpdate,
    setShouldDisplayMappingError,
  ]);
  React.useEffect(() => {
    if (
      validateUpdateSettingInput(
        updateKintoneExportSettingInput,
        formSchema,
        kintoneFormFields,
      )
    ) {
      setShowErrorMessage(false);
      setShouldDisplayMappingError(false);
    }
  }, [
    updateKintoneExportSettingInput,
    formSchema,
    kintoneFormFields,
    setShowErrorMessage,
    setShouldDisplayMappingError,
  ]);

  return (
    <>
      {showErrorMessage && (
        <div className="columns is-vcentered mb-5">
          <div className="column">
            <div className="notification is-danger is-light">
              入力内容にエラーがあります。
            </div>
          </div>
        </div>
      )}
      <div className="columns is-vcentered">
        <div className="column has-text-right buttons">
          <SecondaryButton
            text="手動で実行"
            onClick={() => {
              setIsExportingModalOpen(true);
            }}
            disabled={isExporting || isUpdating}
          />
          <PrimaryButton
            text="更新"
            onClick={handleUpdate}
            disabled={isExporting || isUpdating}
          />
        </div>
      </div>
      <Spinner loading={isUpdating || isExporting} />
    </>
  );
};

type UpdateFormProps = {
  procedure: ProcedureType;
  kintoneFormFields?: KintoneFormFields;
  updateKintoneExportSettingInput: UpdateKintoneExportSettingInput;
  onChange: (
    updateKintoneExportSettingInput: UpdateKintoneExportSettingInput,
  ) => void;
  kintoneAppId: KintoneExportSetting["kintoneAppId"];
  kintoneSubdomain: KintoneExportSetting["kintoneSubdomain"];
  procedureDetailLink: string;
  shouldDisplayMappingError?: boolean;
  showJsonEditor?: boolean;
  showKintoneFieldsList?: boolean;
};

const UpdateForm: React.FC<UpdateFormProps> = ({
  procedure,
  kintoneFormFields,
  updateKintoneExportSettingInput,
  onChange,
  kintoneAppId,
  kintoneSubdomain,
  procedureDetailLink,
  shouldDisplayMappingError,
  showJsonEditor,
  showKintoneFieldsList,
}) => {
  const [usedKintoneFieldsInMeta, setUsedKintoneFieldsInMeta] = React.useState<
    Set<KintoneFormField["code"]>
  >(new Set());

  const nameInputProps = useInputProps({
    value: updateKintoneExportSettingInput.name,
    onChange: (v) => {
      onChange({
        ...updateKintoneExportSettingInput,
        name: v,
      });
    },
    validate: validateName,
  });

  const descriptionInputProps = useInputProps({
    value: updateKintoneExportSettingInput.description,
    onChange: (v) => {
      onChange({
        ...updateKintoneExportSettingInput,
        description: v,
      });
    },
    validate: validateDescription,
  });

  const { auth } = useAuthContextV2();
  const canToggleEnableDisableButton = React.useMemo(() => {
    return (
      auth.isServiceProviderAccount() ||
      auth.checkGroupPermission(
        new KintoneExportSettingGroupPermissionAttached(procedure),
        ["kintone_export.setting.enable_disable"],
      )
    );
  }, [auth, procedure]);

  return (
    <div>
      <div className="columns">
        <div className="column">
          <InlineRadioButton
            label="有効 / 無効"
            onChange={(e) => {
              onChange({
                ...updateKintoneExportSettingInput,
                disabled: e.target.value === "DISABLED",
              });
            }}
            options={[
              {
                name: "有効",
                value: "ENABLED",
              },
              {
                name: "無効",
                value: "DISABLED",
              },
            ]}
            value={
              updateKintoneExportSettingInput.disabled ? "DISABLED" : "ENABLED"
            }
            disabled={!canToggleEnableDisableButton}
          />
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <TextField
            label="設定名称"
            required
            placeholder="設定名称"
            description={"256文字以内で入力してください。"}
            {...nameInputProps}
          />
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <TextField
            label="説明文"
            placeholder="説明文を入力してください"
            description={"256文字以内で入力してください。"}
            {...descriptionInputProps}
          />
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <TextField
            label="スマート申請手続き"
            value={`${procedure.name} (ID: ${procedure.id})`}
            onChange={() => {
              return;
            }}
            required
            placeholder=""
            description={
              <a
                href={procedureDetailLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                手続き設定ページを新しいタブで開く
              </a>
            }
            disabled
          />
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <TextField
            label="kintone アプリ URL"
            value={`https://${kintoneSubdomain}.cybozu.com/k/${kintoneAppId}/`}
            onChange={() => {
              return;
            }}
            required
            placeholder=""
            description={
              <>
                <a
                  href={`https://${kintoneSubdomain}.cybozu.com/k/${kintoneAppId}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  kintone アプリを新しいタブで開く
                </a>
                {showKintoneFieldsList && !!kintoneFormFields && (
                  <>
                    <br />
                    <KintoneFieldsViewer
                      kintoneFormFields={kintoneFormFields}
                    />
                  </>
                )}
              </>
            }
            disabled
          />
        </div>
      </div>

      <KintoneAPITokenInput
        kintoneApiToken={updateKintoneExportSettingInput.kintoneAPIToken}
        onChange={(kintoneAPIToken) => {
          onChange({
            ...updateKintoneExportSettingInput,
            kintoneAPIToken,
          });
        }}
      />

      <MetadataFieldMappingForm
        metadataFieldMapping={
          updateKintoneExportSettingInput.metadataFieldMapping
        }
        onChange={(metadataFieldMapping) => {
          onChange({
            ...updateKintoneExportSettingInput,
            metadataFieldMapping,
          });
        }}
        kintoneFormFields={kintoneFormFields}
        setUsedKintoneFields={setUsedKintoneFieldsInMeta}
        shouldDisplayError={shouldDisplayMappingError}
      />

      <ElementFieldMappingsForm
        elementFieldMappings={
          updateKintoneExportSettingInput.elementFieldMappings
        }
        onChange={(elementFieldMappings) => {
          onChange({
            ...updateKintoneExportSettingInput,
            elementFieldMappings,
          });
        }}
        procedure={procedure}
        kintoneFormFields={kintoneFormFields}
        usedKintoneFields={usedKintoneFieldsInMeta}
        showJsonEditor={showJsonEditor}
      />
    </div>
  );
};
